import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Star, Calendar, User, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Evaluation {
  id: string;
  employee_id: string;
  evaluator_id: string | null;
  evaluation_date: string;
  evaluation_period: string;
  overall_rating: number | null;
  performance_goals: string | null;
  achievements: string | null;
  areas_for_improvement: string | null;
  training_needs: string | null;
  next_review_date: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  department: string;
}

export const EvaluationManagement = () => {
  const { toast } = useToast();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [evaluationDialog, setEvaluationDialog] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState<Evaluation | null>(null);

  const [evaluationForm, setEvaluationForm] = useState({
    employee_id: '',
    evaluator_id: '',
    evaluation_date: '',
    evaluation_period: '',
    overall_rating: '',
    performance_goals: '',
    achievements: '',
    areas_for_improvement: '',
    training_needs: '',
    next_review_date: '',
    status: 'Brouillon'
  });

  useEffect(() => {
    fetchEvaluations();
    fetchEmployees();
  }, []);

  const fetchEvaluations = async () => {
    try {
      const { data, error } = await supabase
        .from('employee_evaluations')
        .select('*')
        .order('evaluation_date', { ascending: false });

      if (error) throw error;
      setEvaluations(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des évaluations:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les évaluations",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, position, department')
        .eq('status', 'active')
        .order('first_name', { ascending: true });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des employés:', error);
    }
  };

  const handleEvaluationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const evaluationData = {
        ...evaluationForm,
        overall_rating: evaluationForm.overall_rating ? parseInt(evaluationForm.overall_rating) : null,
        evaluation_date: evaluationForm.evaluation_date || new Date().toISOString().split('T')[0],
        next_review_date: evaluationForm.next_review_date || null
      };

      if (editingEvaluation) {
        const { error } = await supabase
          .from('employee_evaluations')
          .update(evaluationData)
          .eq('id', editingEvaluation.id);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Évaluation mise à jour avec succès"
        });
      } else {
        const { error } = await supabase
          .from('employee_evaluations')
          .insert([evaluationData]);

        if (error) throw error;

        toast({
          title: "Succès",
          description: "Nouvelle évaluation créée avec succès"
        });
      }

      fetchEvaluations();
      resetForm();
      setEvaluationDialog(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder l'évaluation",
        variant: "destructive"
      });
    }
  };

  const handleEditEvaluation = (evaluation: Evaluation) => {
    setEditingEvaluation(evaluation);
    setEvaluationForm({
      employee_id: evaluation.employee_id,
      evaluator_id: evaluation.evaluator_id || '',
      evaluation_date: evaluation.evaluation_date,
      evaluation_period: evaluation.evaluation_period,
      overall_rating: evaluation.overall_rating?.toString() || '',
      performance_goals: evaluation.performance_goals || '',
      achievements: evaluation.achievements || '',
      areas_for_improvement: evaluation.areas_for_improvement || '',
      training_needs: evaluation.training_needs || '',
      next_review_date: evaluation.next_review_date || '',
      status: evaluation.status
    });
    setEvaluationDialog(true);
  };

  const handleDeleteEvaluation = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette évaluation ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('employee_evaluations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Évaluation supprimée avec succès"
      });

      fetchEvaluations();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'évaluation",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setEditingEvaluation(null);
    setEvaluationForm({
      employee_id: '',
      evaluator_id: '',
      evaluation_date: '',
      evaluation_period: '',
      overall_rating: '',
      performance_goals: '',
      achievements: '',
      areas_for_improvement: '',
      training_needs: '',
      next_review_date: '',
      status: 'Brouillon'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Terminé':
        return <Badge className="bg-green-100 text-green-800">{status}</Badge>;
      case 'En cours':
        return <Badge className="bg-blue-100 text-blue-800">{status}</Badge>;
      case 'Brouillon':
        return <Badge variant="outline">{status}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRatingStars = (rating: number | null) => {
    if (!rating) return 'Non évalué';
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-muted-foreground">({rating}/5)</span>
      </div>
    );
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Employé inconnu';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-sm text-muted-foreground">Chargement des évaluations...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white/80 backdrop-blur-sm shadow-xl border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Star className="h-6 w-6 text-primary" />
              <span>Gestion des évaluations</span>
            </CardTitle>
            <CardDescription>
              Créez et gérez les évaluations de performance des employés
            </CardDescription>
          </div>
          <Dialog open={evaluationDialog} onOpenChange={setEvaluationDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle évaluation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingEvaluation ? 'Modifier l\'évaluation' : 'Nouvelle évaluation'}
                </DialogTitle>
                <DialogDescription>
                  {editingEvaluation ? 'Modifiez les détails de l\'évaluation' : 'Créez une nouvelle évaluation d\'employé'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleEvaluationSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employé *</Label>
                    <Select 
                      value={evaluationForm.employee_id} 
                      onValueChange={(value) => setEvaluationForm({...evaluationForm, employee_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name} - {employee.position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="evaluation_period">Période d'évaluation *</Label>
                    <Input
                      id="evaluation_period"
                      placeholder="Ex: T1 2024, Annuelle 2024"
                      value={evaluationForm.evaluation_period}
                      onChange={(e) => setEvaluationForm({...evaluationForm, evaluation_period: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="evaluation_date">Date d'évaluation</Label>
                    <Input
                      id="evaluation_date"
                      type="date"
                      value={evaluationForm.evaluation_date}
                      onChange={(e) => setEvaluationForm({...evaluationForm, evaluation_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="overall_rating">Note globale (1-5)</Label>
                    <Select 
                      value={evaluationForm.overall_rating} 
                      onValueChange={(value) => setEvaluationForm({...evaluationForm, overall_rating: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 - Insuffisant</SelectItem>
                        <SelectItem value="2">2 - En dessous des attentes</SelectItem>
                        <SelectItem value="3">3 - Conforme aux attentes</SelectItem>
                        <SelectItem value="4">4 - Dépasse les attentes</SelectItem>
                        <SelectItem value="5">5 - Exceptionnel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Statut</Label>
                    <Select 
                      value={evaluationForm.status} 
                      onValueChange={(value) => setEvaluationForm({...evaluationForm, status: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Brouillon">Brouillon</SelectItem>
                        <SelectItem value="En cours">En cours</SelectItem>
                        <SelectItem value="Terminé">Terminé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="performance_goals">Objectifs de performance</Label>
                  <Textarea
                    id="performance_goals"
                    placeholder="Décrivez les objectifs et leur réalisation..."
                    value={evaluationForm.performance_goals}
                    onChange={(e) => setEvaluationForm({...evaluationForm, performance_goals: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="achievements">Réalisations et points forts</Label>
                  <Textarea
                    id="achievements"
                    placeholder="Listez les principales réalisations et points forts..."
                    value={evaluationForm.achievements}
                    onChange={(e) => setEvaluationForm({...evaluationForm, achievements: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="areas_for_improvement">Axes d'amélioration</Label>
                  <Textarea
                    id="areas_for_improvement"
                    placeholder="Identifiez les domaines à améliorer..."
                    value={evaluationForm.areas_for_improvement}
                    onChange={(e) => setEvaluationForm({...evaluationForm, areas_for_improvement: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="training_needs">Besoins en formation</Label>
                    <Textarea
                      id="training_needs"
                      placeholder="Formations recommandées..."
                      value={evaluationForm.training_needs}
                      onChange={(e) => setEvaluationForm({...evaluationForm, training_needs: e.target.value})}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="next_review_date">Prochaine évaluation</Label>
                    <Input
                      id="next_review_date"
                      type="date"
                      value={evaluationForm.next_review_date}
                      onChange={(e) => setEvaluationForm({...evaluationForm, next_review_date: e.target.value})}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEvaluationDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingEvaluation ? 'Mettre à jour' : 'Créer l\'évaluation'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {evaluations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Star className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>Aucune évaluation trouvée</p>
            <p className="text-sm">Créez votre première évaluation d'employé</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employé</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Prochaine évaluation</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {evaluations.map((evaluation) => (
                <TableRow key={evaluation.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{getEmployeeName(evaluation.employee_id)}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{evaluation.evaluation_period}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>{new Date(evaluation.evaluation_date).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getRatingStars(evaluation.overall_rating)}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(evaluation.status)}
                  </TableCell>
                  <TableCell>
                    {evaluation.next_review_date ? (
                      <span className="text-sm">
                        {new Date(evaluation.next_review_date).toLocaleDateString('fr-FR')}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">Non planifiée</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditEvaluation(evaluation)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteEvaluation(evaluation.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};